import { baseApi } from './baseApi';
import { ServiceTypes } from '../constants';
import { FolderManagerMimeTypes, ProjectFromApi } from '../interfaces';
import { mapProjectToStore, getOutputTypesFromDocumentTemplates } from '../helpers';

enum RequestIds {
  GET_PROJECT = 'GET_PROJECT',
}

const mapApiToStore = (item: ProjectFromApi) => ({
  id: item?.ID,
  displayName: item?.DisplayName,
  projectTemplate: item?.ProjectTemplate,
  projectTemplateId: item?.ProjectTemplateID,
  values: item?.Values,
  dataGrids: (item?.DataGrids || []).map((item) => ({
    id: item?.ID,
    slug: item?.Slug,
    displayName: item?.Name,
  })),
  documents: item?.Documents,
  createdAt: item?.CreatedAt,
  updatedAt: item?.UpdatedAt,
  deletedAt: item?.DeletedAt,
});

const mapEntityToStore = (item: any) => ({
  id: item?.ID,
  displayName: item?.DisplayName || item?.Data?.name || `${item?.Data?.first_name} ${item?.Data?.last_name}`,
  type: item?.Type,
  actions: [], // folder manager actions
  projectId: item?.ProjectID,
  createdAt: item?.CreatedAt,
  updatedAt: item?.UpdatedAt,
  deletedAt: item?.DeletedAt,
});

const mapProjectTypeToStore = (item: any) => ({
  id: item.ID,
  name: item.DisplayName,
  type: FolderManagerMimeTypes.FOLDER,
  itemData: item,
  updatedAt: item.UpdatedAt,
  actions: [],
});

const mapProject = (item: ProjectFromApi) => {
  const templateFeatures = item?.ProjectTemplate?.Features || [];

  return {
    ...item,
    outputTypes: getOutputTypesFromDocumentTemplates((item?.ProjectTemplate as any)?.DocumentTemplates),
    Metadata: item?.Metadata || {
      features: templateFeatures || [],
    },
  };
};

export const projectsService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllProjects: builder.query<ProjectFromApi[], { templateId?: number } | void>({
      query: (args) => ({
        url: '/projects',
        method: 'GET',
        params: args?.templateId ? { templateId: args.templateId } : undefined,
      }),
      transformResponse: (result: IApiV2Response<ProjectFromApi>) => result.data,
      providesTags: () => [{ type: ServiceTypes.PROJECTS, id: 'ALL' }],
    }),
    getProjects: builder.query<WorkspaceFromStore[], null>({
      query: () => ({
        url: '/projects/me',
        method: 'GET',
      }),
      transformResponse: (result: any) => result.data.map(mapProjectToStore),
      providesTags: () => [{ type: ServiceTypes.PROJECTS, id: 'LIST' }],
    }),
    getRecentProjects: builder.query<WorkspaceFromStore[], void>({
      query: () => ({
        url: '/projects/me/recent',
        method: 'GET',
      }),
      transformResponse: (result: any) => result.data.map(mapProjectToStore),
      providesTags: () => [{ type: ServiceTypes.PROJECTS, id: 'RECENT' }, { type: ServiceTypes.PROJECTS }],
    }),
    getSharedWithMeProjects: builder.query<WorkspaceFromStore[], void>({
      query: () => ({
        url: '/projects/me/shared',
        method: 'GET',
      }),
      transformResponse: (result: any) => result.data.map(mapProjectToStore),
      providesTags: () => [{ type: ServiceTypes.PROJECTS, id: 'SHARED' }],
    }),
    createProject: builder.mutation({
      query: ({ projectTemplateId }) => ({
        url: `/projects`,
        method: 'POST',
        body: {
          projectTemplateId: projectTemplateId,
        },
      }),
      transformResponse: (result: any) => mapApiToStore(result.data),
      invalidatesTags: () => [{ type: ServiceTypes.PROJECTS, id: 'LIST' }],
    }),
    updateProject: builder.mutation({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: () => [
        { type: ServiceTypes.PROJECTS, id: RequestIds.GET_PROJECT },
        {
          type: ServiceTypes.PROJECTS,
          id: 'LIST',
        },
      ],
    }),
    deleteProject: builder.mutation<void, { projectId: number }>({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}`,
        method: 'DELETE',
      }),
      invalidatesTags: () => [
        { type: ServiceTypes.PROJECTS, id: RequestIds.GET_PROJECT },
        { type: ServiceTypes.PROJECTS, id: 'LIST' },
      ],
    }),
    getProjectTypes: builder.query<void, { projectId: number | string }>({
      query: ({ projectId }) => ({
        url: `/projects/${+projectId}/types`,
        method: 'GET',
      }),
      transformResponse: (result: any) => result.data.map(mapProjectTypeToStore), // @todo - validate response
      providesTags: (result, error, { projectId }) => [
        { type: ServiceTypes.PROJECTS, id: `PROJECT_TYPES_${projectId}` },
      ],
    }),
    getProjectEntitiesByType: builder.query<void, { projectId: number; entityType: string }>({
      query: ({ projectId, entityType }) => ({
        url: `/projects/${projectId}/entities/${entityType}`,
        method: 'GET',
      }),
      transformResponse: (result: any) => result.data.map(mapEntityToStore),
      providesTags: (result, error, { projectId, entityType }) => [
        { type: ServiceTypes.PROJECTS, id: `ENTITY_TYPE_${projectId}_${entityType}` }, // Provide tags for project entities by type
      ],
    }),
    getProjectById: builder.query<any, { projectId: number }>({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}`,
        method: 'GET',
      }),
      transformResponse: (result: any) => mapProject(result.data),
      providesTags: (result, error, { projectId }) => [{ type: ServiceTypes.PROJECTS, id: projectId }],
    }),
    getWorkspaceGrantsByMe: builder.query<UserGrants, { projectId: number }>({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}/me`,
        method: 'GET',
      }),
      transformResponse: (result: IApiResponse<UserGrants>) => result.data,
      providesTags: (result, error, { projectId }) => [{ type: ServiceTypes.PROJECTS, id: `GRANTS_ME_${projectId}` }],
    }),
    getWorkspaceGrants: builder.query<IGrant[], { workspaceId: number }>({
      query: ({ workspaceId }) => `/projects/${workspaceId}/grants`,
      transformResponse: (result: IApiResponse<IGrant[]>) => result.data,
      providesTags: (result, error, { workspaceId }) => [{ type: ServiceTypes.PROJECTS, id: `GRANTS_${workspaceId}` }],
    }),
    shareWorkspace: builder.mutation<void, { workspaceId: number; data: SharePayload }>({
      query: ({ workspaceId, data }) => ({
        url: `/projects/${workspaceId}/grants`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: () => [{ type: ServiceTypes.PROJECTS }],
    }),
    revokeWorkspaceGrant: builder.mutation<void, { workspaceId: number; data: SharePayload }>({
      query: ({ workspaceId, data }) => ({
        url: `/projects/${workspaceId}/grants`,
        method: 'DELETE',
        body: data,
      }),
      invalidatesTags: () => [{ type: ServiceTypes.PROJECTS }],
    }),
    uploadProjectFile: builder.mutation({
      query: ({ projectId, data }) => {
        const formData = new FormData();
        formData.append('file', data.file);

        return {
          url: `/projects/${projectId}/upload`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ({ projectId }) => [{ type: ServiceTypes.PROJECTS, id: projectId }],
    }),
  }),
});

export const {
  useGetAllProjectsQuery,
  useGetProjectsQuery,
  useGetRecentProjectsQuery,
  useDeleteProjectMutation,
  useCreateProjectMutation,
  useGetProjectByIdQuery,
  useLazyGetProjectByIdQuery,
  useGetProjectTypesQuery,
  useUpdateProjectMutation,
  useGetWorkspaceGrantsQuery,
  useShareWorkspaceMutation,
  useRevokeWorkspaceGrantMutation,
  useGetWorkspaceGrantsByMeQuery,
  useGetSharedWithMeProjectsQuery,
  useGetProjectEntitiesByTypeQuery,
} = projectsService;
